import {HttpClient, HttpHeaders} from '@angular/common/http';
import {inject, Injectable, NgZone} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {AuthenticationTokenBearerService} from '../auth/authentication-token-bearer.service';
import {OidcToken} from '../auth/oidc/oidc-token';
import {ConfigurationService} from '../core/configuration/services/configuration.service';
import {NavigationService} from './navigation.service';
import {NotificationsService} from './notifications.service';
import {ThinClientModeService} from './thin-client-mode.service';
import {ClientUpdateRequest, UpdateDownloadInfo, UpdateVersionInfo, UpdateVersionResponse} from './thin-client.payload';

@Injectable({
  providedIn: 'root'
})
export class ThinClientService {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly navigationService: NavigationService = inject(NavigationService);
  private readonly authenticationTokenBearerService: AuthenticationTokenBearerService = inject(AuthenticationTokenBearerService);
  private readonly zone: NgZone = inject(NgZone);
  private readonly notificationsService: NotificationsService = inject(NotificationsService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);

  currentVersion: string;
  terminalServerInstallation: boolean = false;

  constructor() {
    ThinClientModeService.thinClientMode = navigator.userAgent.indexOf('AwpThinClient') >= 0;
    ThinClientService.logThinClientMode();
  }

  static logThinClientMode(): void {
    console.log('[Webapp] [ThinClientService] running in thin client: ' + ThinClientModeService.thinClientMode);
  }

  private static isNewerVersionAvailable(currentVersion: string, latestVersion: string): boolean {
    if (latestVersion !== undefined) {
      const currentVersionParts = currentVersion.split('.');
      const latestVersionParts = latestVersion.split('.');
      const minLength = currentVersion.length > latestVersion.length ? latestVersion.length : currentVersion.length;
      for (let i = 0; i < minLength; ++i) {
        if (Number(currentVersionParts[i]) < Number(latestVersionParts[i])) {
          return true;
        }
      }
    }
    return false;
  }

  initialize(): void {
    if (ThinClientModeService.thinClientMode) {
      this.registerNavigateByUriSchemeCallback();
      this.initializeUpdateLogConfig();
      this.hasBeenCalledByApas();
      this.hasBeenCalledFromTerminalServer();
      this.notifyReady();
      this.checkForUpdate();
    }
  }

  initializeUpdateLogConfig(): void {
    // subscribe also immediately calls the method
    this.authenticationTokenBearerService.oidcTokenSubject.subscribe(oidcToken => this.updateLogConfig(oidcToken));
  }

  /**
   * Updates the ThinClient remote logging configuration: tells the ThinClient the CaVORS sessions ID
   * as well as information where and how to send its .NET client logs to the logcollector service.
   */
  private updateLogConfig(oidcToken: OidcToken) {
    const cavorsSessionId = sessionStorage.getItem('cavors-session-id');
    const url = window.location.origin + '/api/logcollector/logs';
    const authHeaders = oidcToken.getHeaders();
    const putBody = {
      cavorsSessionId,
      url,
      authHeaders
    };
    this.httpClient.put('/api/client/logs/config', putBody).pipe(
      catchError(error => of(error))
    ).subscribe();
  }

  checkForUpdate(): void {
    this.getCurrentVersion().subscribe({
      next: (currentVersion: any) => {
        this.currentVersion = currentVersion;
        this.getLatestVersion().subscribe({
          next: (availableVersions: UpdateVersionResponse) => {
            let versions: UpdateVersionInfo = this.terminalServerInstallation
              ? availableVersions.terminalServer
              : availableVersions.standard;
            this.evaluateUpdate(currentVersion, versions);
          },
          error: (error) => console.log('Can not get latest version of AWP Thin client', error)
        });
      },
      error: (error) => console.log('Can not get current version of AWP Thin client', error)
    });
  }

  private notifyReady(): void {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    const body = {ready: true};
    this.httpClient.put('/api/client/status/frontend', body, {headers}).pipe(
      catchError(error => of(error))
    ).subscribe();
  }

  /**
   * ThinClient installation will register the awp:/ protocol on the user's machine. This can be used to open the
   * client with arbitrary parameters. If the user opens e.g. 'awp:/start?app=vehicledetails&vin=WBAAC11080AB12345',
   * the ThinClient will call a function window.navigateByUriScheme with
   * '/start?app=vehicledetails&vin=WBAAC11080AB12345'.
   *
   * Params for integrated apps can be passed via a JSON object in the 'params' parameter, e.g.:
   * awp:/start?app=vehicledetails&vin=WBAAC11080AB12345&params={"customParam1":"value1","customParam2":"value2"}
   */
  private registerNavigateByUriSchemeCallback(): void {
    (window as any).navigateByUriScheme = (decodedParamString: string) => {
      decodedParamString = decodeURIComponent(decodedParamString);
      console.log(`window.navigateByUriScheme called with "${decodedParamString}".`);

      this.zone.run(() => {
        const searchParams: URLSearchParams = new URLSearchParams(decodedParamString.replace('/start?', ''));
        const app: string | undefined = this.extractAppFromSearchParams(searchParams);
        const paramsForApps: Record<string, string> | undefined = this.extractParamsForAppsFromSearchParams(searchParams);
        // INTERIM ISPI-481001: use navigateForcingRouteChange (instead of navigateTo) so that a repeated ISTA
        // jump with an identical FORK_CONTEXT_ID still produces a fresh queryParams emission and logs out again.
        // Revert to navigationService.navigateTo(...) once ISTA sends a user-id (the user-id check is idempotent).
        const appPath: string = app ? `/${app}` : '';
        this.navigationService.navigateForcingRouteChange(
          // target URL including AWP parameters such as vin or type code
          `${appPath}?${searchParams.toString()}`,
          // parameters for integrated apps (passed as web component attributes or iframe query parameters)
          paramsForApps ? {state: {queryParams: paramsForApps}} : undefined
        );
      });
    };
  }

  /**
   * Extract params for integrated apps from the 'params' URL parameter.
   *
   * The 'params' parameter is intentionally kept in the URLSearchParams object so that it remains part of the
   * navigation URL (i.e. the route query params). This is required for the re-login logic in
   * {@link ServiceCaseBarComponent} which inspects the 'params' query parameter for a 'FORK_CONTEXT_ID' to decide
   * whether the current user needs to be logged out (ISTA jump). Stripping it here would mirror it only into the
   * router state, which the re-login check does not see, so no logout would happen in the AWP Client.
   * See https://atc.bmwgroup.net/jira/browse/ISPI-481001.
   *
   * @param searchParams URLSearchParams object
   */
  private extractParamsForAppsFromSearchParams(searchParams: URLSearchParams): Record<string, string> | undefined {
    let paramsForApps: Record<string, string> | undefined;
    if (searchParams.get('params')) {
      try {
        paramsForApps = JSON.parse(searchParams.get('params'));
      } catch (e) {
        console.error("Could not parse 'params' parameter of navigateByUriScheme call.", e);
      }
    }
    return paramsForApps;
  }

  /**
   * Extract app name from the 'app' URL parameter. Removes the parameter from the URLSearchParams object.
   * @param searchParams URLSearchParams object
   */
  private extractAppFromSearchParams(searchParams: URLSearchParams): string | undefined {
    const app: string = searchParams.get('app');
    if (app) {
      searchParams.delete('app');
    }
    return app;
  }

  private getCurrentVersion() {
    return this.httpClient.get('/api/client/version', {responseType: 'text'});
  }

  private getLatestVersion() {
    return this.httpClient.get(ConfigurationService.getUrlForPortalHost('updateService'));
  }

  /**
   * Triggers a ThinClient update if current version is older than the latest stable and if not running on a terminal
   * server. Will only show a notification in case of the latter.
   *
   * @param currentVersion current version of the thin client
   * @param availableVersions available versions of the thin client
   */
  private evaluateUpdate(currentVersion: string, availableVersions: UpdateVersionInfo): void {
    if (currentVersion !== undefined && availableVersions !== undefined) {
      const latestVersion = availableVersions.latestStable;
      if (ThinClientService.isNewerVersionAvailable(currentVersion, latestVersion)) {
        if (!this.terminalServerInstallation) {
          const updateData: UpdateDownloadInfo = availableVersions.versions[latestVersion];
          this.triggerUpdate(latestVersion, updateData.downloadUrl, updateData.requiresAuth);
        } else {
          this.notificationsService.showInfoNotification(
            this.translate.instant('thinClient.terminalServerUpdateAvailable'),
            0
          );
        }
      }
    }
  }

  private triggerUpdate(version: string, downloadUrl: string, requiresAuth: boolean) {
    const authHeaders = requiresAuth
      ? this.authenticationTokenBearerService.oidcTokenSubject.getValue().getHeaders()
      : {};
    const postBody: ClientUpdateRequest = {
      version,
      downloadUrl,
      authHeaders
    };
    this.httpClient.post('/api/client/update', postBody).pipe(
      catchError(error => of(error))
    ).subscribe(() => {
      this.notificationsService.showInfoNotification(
        this.translate.instant('thinClient.updateAfterRestart'),
        0
      );
    });
  }

  private hasBeenCalledByApas() {
    this.route.queryParams.subscribe((params) => {
      if (params?.mode === 'apas') {
        ThinClientModeService.apasMode = true;
      }
    });
  }

  private hasBeenCalledFromTerminalServer(): void {
    if (localStorage.getItem('terminalServer') === 'true') {
      this.terminalServerInstallation = true;
    }
  }
}
